import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getUsers,
  deleteUser,
  updateUserAccountStatus,
  updateUserRole,
  createAdminUser,
  updateUserAdminPermissions,
} from '../../services/admin';
import type { User } from '../../types/admin';
import {
  ALL_ADMIN_PERMISSIONS,
  type AdminPermissionKey,
} from '../../utils/adminPermissions';

const AUTH_STORAGE_KEY = 'tellurvision_auth';

type StoredAuthRecord = {
  id?: string;
  _id?: string;
  user?: StoredAuthRecord;
  adminPermissions?: AdminPermissionKey[];
};

const syncLocalAdminPermissions = (userId: string, next: AdminPermissionKey[]) => {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as StoredAuthRecord;
    const inner =
      parsed && parsed.user && typeof parsed.user === 'object' ? parsed.user : parsed;
    const storedId = String(inner.id || inner._id || '');
    if (storedId !== userId) return;
    inner.adminPermissions = next;
    window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(parsed));
  } catch {
    // best-effort only; ignore storage errors
  }
};

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState<boolean>(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const fetchedUsers = await getUsers();
        const shapedUsers: User[] = fetchedUsers.map((u) => ({
          id: u.id,
          name: u.username || u.email,
          email: u.email,
          role: u.role,
          accountStatus: u.accountStatus,
          adminPermissions: u.adminPermissions || [],
          createdAt: u.createdAt,
          updatedAt: u.updatedAt,
        }));
        setUsers(shapedUsers);
      } catch (err: unknown) {
        const message =
          typeof err === 'object' &&
          err !== null &&
          'message' in err &&
          typeof (err as Record<string, unknown>).message === 'string'
            ? ((err as Record<string, unknown>).message as string)
            : 'Failed to fetch users';

        setError(message);
      } finally {
        setLoading(false);
      }
    };

    void fetchUsers();
  }, []);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!newEmail || !newPassword) {
      setError('Email and initial password are required');
      return;
    }

    setCreating(true);
    try {
      const created = await createAdminUser({
        username: newName || undefined,
        email: newEmail,
        password: newPassword,
      });

      const shaped: User = {
        id: created.id,
        name: created.username || created.email,
        email: created.email,
        role: created.role,
        accountStatus: created.accountStatus,
        adminPermissions: created.adminPermissions || [],
        createdAt: created.createdAt,
        updatedAt: created.updatedAt,
      };

      setUsers((prev) => [shaped, ...prev]);
      setNewName('');
      setNewEmail('');
      setNewPassword('');
    } catch (err: unknown) {
      let message = 'Failed to create user';
      if (
        typeof err === 'object' &&
        err !== null &&
        'message' in err &&
        typeof (err as Record<string, unknown>).message === 'string'
      ) {
        message = (err as Record<string, unknown>).message as string;
      }
      // Surface common backend validation messages more clearly.
      if (message.includes('Password must be at least')) {
        message = 'Initial password must be at least 6 characters long.';
      } else if (message.includes('already exists')) {
        message = 'A user with this email already exists. Try a different email.';
      } else if (message.includes('Email is required')) {
        message = 'Email is required to create a user.';
      }
      setError(message);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      await deleteUser(userId);
      setUsers((prev) => prev.filter((user) => user.id !== userId));
    } catch {
      setError('Failed to delete user');
    }
  };

  const handleStatusChange = async (
    userId: string,
    status: 'good' | 'past_due' | 'suspended',
  ) => {
    try {
      await updateUserAccountStatus(userId, status);
      setUsers((prev) =>
        prev.map((user) => (user.id === userId ? { ...user, accountStatus: status } : user)),
      );
    } catch {
      setError('Failed to update account status');
    }
  };

  const handlePermissionsToggle = async (
    userId: string,
    permission: AdminPermissionKey,
    checked: boolean,
  ) => {
    try {
      const target = users.find((u) => u.id === userId);
      if (!target) return;

      const current = Array.isArray(target.adminPermissions)
        ? (target.adminPermissions as AdminPermissionKey[])
        : [];

      const next = checked
        ? Array.from(new Set([...current, permission]))
        : current.filter((p) => p !== permission);

      await updateUserAdminPermissions(userId, next);

      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, adminPermissions: next } : u)),
      );
      syncLocalAdminPermissions(userId, next);
    } catch {
      setError('Failed to update admin permissions');
    }
  };

  const handleSetPermissions = async (userId: string, next: AdminPermissionKey[]) => {
    try {
      await updateUserAdminPermissions(userId, next);
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, adminPermissions: next } : u)));
      syncLocalAdminPermissions(userId, next);
    } catch {
      setError('Failed to update admin permissions');
    }
  };

  return (
    <div className="space-y-3">

      <section className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3 text-xs text-slate-800 dark:text-slate-200">
        <h3 className="mb-2 text-xs font-semibold text-slate-700 dark:text-slate-300">Add a new user</h3>
        <p className="mb-2 text-[11px] text-slate-500 dark:text-slate-400">
          Admins can invite a new user by creating an account with an initial password. Share the
          credentials securely with the user; once logged in they can change their password from the
          My account page.
        </p>
        <form onSubmit={handleCreateUser} className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col">
            <label className="mb-1 text-[11px] text-slate-600 dark:text-slate-400">Name (optional)</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="w-40 rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="flex flex-col">
            <label className="mb-1 text-[11px] text-slate-600 dark:text-slate-400">Email</label>
            <input
              type="email"
              required
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="w-56 rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="flex flex-col">
            <label className="mb-1 text-[11px] text-slate-600 dark:text-slate-400">Initial password</label>
            <input
              type="password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-40 rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <button
            type="submit"
            disabled={creating}
            className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-60 focus:outline-none focus:ring-1 focus:ring-blue-500"
         >
            {creating ? 'Creating…' : 'Create user'}
          </button>
        </form>
      </section>

      {loading && <p className="text-sm text-slate-500 dark:text-slate-400">Loading users...</p>}
      {error && !loading && <p className="text-sm text-red-600">{error}</p>}

      {!loading && !error && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              <tr>
                <th className="px-3 py-2">ID</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Admin privileges</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {users.map((user) => (
                <tr key={user.id}>
                  <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">{user.id}</td>
                  <td className="px-3 py-2 text-sm text-slate-900 dark:text-slate-100">{user.name}</td>
                  <td className="px-3 py-2 text-sm text-slate-700 dark:text-slate-300">{user.email}</td>
                  <td className="px-3 py-2 text-sm text-slate-700 dark:text-slate-300">
                    <select
                      value={user.role === 'admin' ? 'admin' : 'user'}
                      onChange={async (e) => {
                        const nextRole = e.target.value as 'admin' | 'user';
                        try {
                          await updateUserRole(user.id, nextRole);
                          setUsers((prev) =>
                            prev.map((u) => (u.id === user.id ? { ...u, role: nextRole } : u)),
                          );
                        } catch {
                          setError('Failed to update user role');
                        }
                      }}
                      className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-800 dark:text-slate-200 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="user">Standard user</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700 dark:text-slate-300">
                    <select
                      value={user.accountStatus || 'good'}
                      onChange={(e) =>
                        handleStatusChange(
                          user.id,
                          e.target.value as 'good' | 'past_due' | 'suspended',
                        )
                      }
                      className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-800 dark:text-slate-200 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="good">Good</option>
                      <option value="past_due">Past due</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-700 dark:text-slate-300 align-top">
                    <div className="space-y-1">
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">Creator tools</p>
                      <div className="flex flex-wrap gap-1">
                        {ALL_ADMIN_PERMISSIONS.map((perm) => (
                          <label
                            key={perm}
                            className="inline-flex items-center gap-1 rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-1.5 py-0.5 text-[10px] text-slate-700 dark:text-slate-300"
                          >
                            <input
                              type="checkbox"
                              className="h-3 w-3 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                              checked={Array.isArray(user.adminPermissions)
                                ? (user.adminPermissions as AdminPermissionKey[]).includes(perm)
                                : false}
                              onChange={(e) =>
                                handlePermissionsToggle(user.id, perm, e.target.checked)
                              }
                              disabled={user.role !== 'admin'}
                            />
                            <span>
                              {perm === 'payments'
                                ? 'Payments'
                                : perm === 'campaignPackages'
                                  ? 'Campaign packages'
                                  : perm === 'adminGuide'
                                    ? 'Admin guide'
                                    : perm === 'adAnalytics'
                                      ? 'Ad analytics'
                                      : 'Billboard tools'}
                            </span>
                          </label>
                        ))}
                      </div>
                      <p className="text-[10px] text-slate-500 dark:text-slate-400">
                        Checked tools have admin access.
                      </p>
                      <div className="mt-1 flex flex-wrap gap-1 text-[10px]">
                        <button
                          type="button"
                          className="inline-flex items-center rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-1.5 py-0.5 text-slate-600 dark:text-slate-400 hover:bg-slate-50"
                          disabled={user.role !== 'admin'}
                          onClick={() => handleSetPermissions(user.id, ALL_ADMIN_PERMISSIONS)}
                        >
                          Full admin (all tools)
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-1.5 py-0.5 text-slate-600 dark:text-slate-400 hover:bg-slate-50"
                          disabled={user.role !== 'admin'}
                          onClick={() => handleSetPermissions(user.id, [])}
                        >
                          Clear all tools
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right space-x-2">
                    <Link
                      to={`/admin/users/${user.id}`}
                      className="inline-flex items-center rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-2 py-1 text-xs font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100"
                    >
                      View
                    </Link>
                    <Link
                      to={`/admin/users/${user.id}#payment-history`}
                      className="inline-flex items-center rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
                    >
                      Payments
                    </Link>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="inline-flex items-center rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {!users.length && (
                <tr>
                  <td colSpan={7} className="px-3 py-4 text-center text-xs text-slate-500 dark:text-slate-400">
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
